import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import { useAuth } from '../context/AuthContext';
import { FiLogOut } from 'react-icons/fi';
import SocialIcons from './SocialIcons';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationBell from './NotificationBell';
import { useLanguage } from '../context/LanguageContext';
import { NFT_CONTRACT_ADDRESS } from '../config/contracts';
import './Header.css';

const Header = () => {
  const navigate = useNavigate();
  const { account, isConnected, disconnectWallet, web3 } = useWeb3();
  const { isAuthenticated, user, logout } = useAuth();
  const { t } = useLanguage();
  const [isOnChainAdmin, setIsOnChainAdmin] = useState(false);

  useEffect(() => {
    const check = async () => {
      try {
        if (!web3 || !account || !NFT_CONTRACT_ADDRESS) {
          setIsOnChainAdmin(false);
          return;
        }
        // Avoid web3 Contract subscription manager (prevents MaxListeners leaks in dev)
        // owner() signature = 0x8da5cb5b
        const data = '0x8da5cb5b';
        const result = await web3.eth.call({ to: NFT_CONTRACT_ADDRESS, data });
        const owner = result ? `0x${String(result).slice(-40)}` : '';
        setIsOnChainAdmin(!!owner && owner.toLowerCase() === account.toLowerCase());
      } catch {
        setIsOnChainAdmin(false);
      }
    };
    check();
  }, [web3, account]);

  const handleLogout = () => {
    logout();
    disconnectWallet();
    navigate('/');
  };

  return (
    <header className="header">
      <div className="header-container">
        <a href="https://phonestream.online" className="logo">
          {/* Clean phone icon + crisp CSS wordmark (cyan → magenta) — far more
              legible than a baked-image wordmark at header size. */}
          <img src="/logo-icon.png" alt="PHONESTREAM" className="logo-image" />
          <span className="logo-text">PHONESTREAM</span>
        </a>

        <nav className="header-nav">
          <Link to="/store" className="nav-link">
            {t('store')}
          </Link>
          <Link to="/play" className="nav-link">
            {t('play')}
          </Link>
          <Link to="/marketplace" className="nav-link">
            {t('marketplace')}
          </Link>
          {(isConnected || isAuthenticated) && (
            <>
              <Link to="/dashboard" className="nav-link">
                {t('dashboard')}
              </Link>
              {(isAuthenticated && user?.isAdmin) || (isConnected && isOnChainAdmin) ? (
                <Link to="/admin" className="nav-link">
                  {t('admin')}
                </Link>
              ) : null}
            </>
          )}
        </nav>

        <div className="header-actions">
          <SocialIcons variant="navbar" />

          <LanguageSwitcher />

          {/* In-app notification bell — only when signed in (the API requires JWT).
              Backend scopes results by persona: admin sees admin-room, user sees own. */}
          {isAuthenticated && <NotificationBell />}

          {isConnected && account && (
            <div className="wallet-info">
              <span className="wallet-address">
                {account.substring(0, 6)}...{account.substring(account.length - 4)}
              </span>
            </div>
          )}

          {isAuthenticated && (
            <button
              className="logout-btn"
              onClick={handleLogout}
              aria-label="Logout"
            >
              <FiLogOut />
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
